import { auth } from "../firebase/firebaseConfig";
import { SECURE_SPORTS_API_BASE, SECURE_SPORTS_API_CONFIGURED } from "./config";
import { __resetMlbStaticDataForTests, loadStaticMlbBoard, loadStaticMlbProjection, mlbStaticDataEnabled } from "./mlbStaticDataClient";
import { loadStaticCustomerSport, productionStaticCustomerSportEnabled } from "./staticCustomerSportsClient";
import { isProductionDashboardHost, productionOffering } from "./productionOfferingRegistry";
const LOCAL_PREVIEW_BUILD = process.env.REACT_APP_PWG_PRODUCTION_RELEASE !== "1";
let mlbRuntimeOverride = null;

export const mlbDashboardSource = (runtime = null) => {
  const resolved = runtime || mlbRuntimeOverride || (
    typeof window === "undefined" ? {} : window.location
  );
  const hostname = String(resolved.hostname || "").toLowerCase();
  const port = String(resolved.port || "");
  if (/^(localhost|127\.0\.0\.1)$/.test(hostname) && port === "3000") return "local-json";
  if (/(?:^|\.)playswithguru\.com$/.test(hostname)) return "governed-gcs";
  return "unsupported";
};

export const secureSportsDataEnabled = () =>
  String(process.env.REACT_APP_SECURE_SPORTS_DATA || "").trim().toLowerCase() === "true";

export const secureSportsDataEnabledFor = (sport) => {
  if (secureSportsDataEnabled()) return true;
  const normalized = String(sport || "").trim().toUpperCase();
  return normalized === "MLB" && (
    mlbDashboardSource() !== "unsupported" ||
    mlbStaticDataEnabled() ||
    String(process.env.REACT_APP_SECURE_SPORTS_MLB || "").trim().toLowerCase() === "true"
  );
};

export const loadMlbBoard = (options = {}) => {
  clearAdminMlbShardCache();
  if (options.explicitDiagnosticSource === "cloud-run-admin") return loadAdminMlbBoard(options);
  const source = mlbDashboardSource();
  if (source === "local-json") return loadLocalMlbBoard(options);
  if (source === "governed-gcs") return loadStaticMlbBoard(options);
  throw new Error("MLB dashboard data source is unavailable for this environment.");
};

export const loadMlbProjection = (row, version = "") => {
  // Admin game shards already contain the complete private projection row.
  // Never send an admin row through the customer projection endpoint.
  if (row?.__pwgAdmin === true) return Promise.resolve(row);
  const source = mlbDashboardSource();
  if (source === "local-json") return loadLocalMlbProjection(row, version);
  if (source === "governed-gcs") return loadStaticMlbProjection(row, version);
  return Promise.reject(new Error("MLB projection source is unavailable for this environment."));
};

const mlbCache = {
  versionId: "",
  payload: null,
  load: null,
  generation: 0,
  pages: new Map(),
};

const versionIdOf = (payload) => String(payload?.version?.version_id || "").trim();
const projectionIdOf = (row) => String(
  row?.["Projection ID"] ?? row?.ProjectionID ?? row?.projection_id ?? ""
).trim();

const SHA256 = /^[0-9a-f]{64}$/;
const LOCAL_MLB_CANDIDATE_PATH = "/data/mlb-props.json";
const LOCAL_MLB_MANIFEST_PATH = "/data/mlb-local-candidate.json";
const LOCAL_SOCCER_CANDIDATE_PATH = "/data/soccer-props.json";
const LOCAL_SOCCER_MANIFEST_PATH = "/data/soccer-local-candidate.json";
const LOCAL_NFL_CANDIDATE_PATH = "/data/nfl-props.json";
const LOCAL_NFL_MANIFEST_PATH = "/data/nfl-local-candidate.json";
let localMlbOriginOverride = null;
const isLocalMlbOrigin = () => localMlbOriginOverride === null
  ? LOCAL_PREVIEW_BUILD && mlbDashboardSource() === "local-json"
  : localMlbOriginOverride;

const localMlbCache = { payload: null, load: null };
const localSoccerCache = { payload: null, load: null };
const localNflCache = { payload: null, load: null };

async function sha256Response(response) {
  const bytes = await response.arrayBuffer();
  if (!globalThis.crypto?.subtle) throw new Error("Local MLB candidate checksum validation is unavailable.");
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return {
    checksum: [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join(""),
    text: new TextDecoder().decode(bytes), size: bytes.byteLength,
  };
}

export function validateLocalMlbCandidate(payload, manifest, checksum, size) {
  if (!manifest || manifest.schema_version !== "PWG_MLB_LOCAL_REVIEW_V1" || manifest.status !== "VERIFIED") {
    throw new Error("Local MLB promotion evidence is missing or invalid.");
  }
  if (!SHA256.test(String(manifest.candidate_checksum || "")) || checksum !== manifest.candidate_checksum) {
    throw new Error("Local MLB candidate checksum does not match promotion evidence.");
  }
  if (manifest.artifact_path !== LOCAL_MLB_CANDIDATE_PATH || Number(manifest.artifact_size) !== Number(size)) {
    throw new Error("Local MLB promotion evidence points to a different artifact.");
  }
  if (!manifest.generated_at || Number.isNaN(Date.parse(manifest.generated_at))) {
    throw new Error("Local MLB promotion evidence timestamp is invalid.");
  }
  if (!manifest.acquisition_id || !/^\d{4}-\d{2}-\d{2}$/.test(String(manifest.operating_date || ""))) {
    throw new Error("Local MLB candidate identity is incomplete.");
  }
  const rows = Array.isArray(payload?.props) ? payload.props : [];
  const ids = rows.map(projectionIdOf);
  const uniqueIds = new Set(ids);
  if (!rows.length || ids.some((value) => !value) || uniqueIds.size !== rows.length) {
    throw new Error("Local MLB candidate Projection IDs are missing or duplicated.");
  }
  if (rows.length !== Number(manifest.row_count) || uniqueIds.size !== Number(manifest.projection_id_count)) {
    throw new Error("Local MLB candidate row or Projection-ID count does not match promotion evidence.");
  }
  return rows;
}

function localLineType(row) {
  const value = String(row?.["Line Type"] || row?.["Source Line Type"] || "").trim().toLowerCase();
  return value === "primary" ? "regular" : value;
}

function localMlbGames(rows) {
  const games = new Map();
  for (const row of rows) {
    const gameId = String(row?.["Game ID"] || "").trim();
    if (!gameId) continue;
    const current = games.get(gameId) || {
      game_id: gameId,
      matchup: `${row?.["Away Team"] || ""} @ ${row?.["Home Team"] || ""}`.trim(),
      away_team: row?.["Away Team"] || "",
      home_team: row?.["Home Team"] || "",
      game_date: String(row?.GameDate || row?.["Game Date"] || "").slice(0, 10),
      game_time: row?.GameTime || "",
      game_start_timestamp: row?.["Game Start Timestamp"] || "",
      projection_id_count: 0,
      line_type_counts: { regular: 0, goblin: 0, demon: 0 },
    };
    current.projection_id_count += 1;
    const type = localLineType(row);
    if (Object.hasOwn(current.line_type_counts, type)) current.line_type_counts[type] += 1;
    games.set(gameId, current);
  }
  return [...games.values()];
}

async function loadLocalMlbCandidate() {
  if (!isLocalMlbOrigin()) return null;
  if (localMlbCache.payload) return localMlbCache.payload;
  if (localMlbCache.load) return localMlbCache.load;
  localMlbCache.load = Promise.all([
    fetch(LOCAL_MLB_CANDIDATE_PATH, { cache: "no-store", credentials: "same-origin" }),
    fetch(LOCAL_MLB_MANIFEST_PATH, { cache: "no-store", credentials: "same-origin" }),
  ]).then(async ([candidateResponse, manifestResponse]) => {
    if (!candidateResponse.ok || !manifestResponse.ok) {
      throw new Error("Local MLB candidate or promotion evidence is unavailable.");
    }
    const [candidate, manifest] = await Promise.all([
      sha256Response(candidateResponse),
      manifestResponse.json(),
    ]);
    let payload;
    try { payload = JSON.parse(candidate.text); } catch { throw new Error("Local MLB candidate is malformed."); }
    const rows = validateLocalMlbCandidate(payload, manifest, candidate.checksum, candidate.size);
    const value = {
      props: rows,
      injuryReport: Array.isArray(payload?.injury_report) ? payload.injury_report : [],
      games: localMlbGames(rows),
      manifest,
      version: { version_id: `local_${manifest.candidate_checksum.slice(0, 24)}` },
    };
    localMlbCache.payload = value;
    return value;
  }).finally(() => { localMlbCache.load = null; });
  return localMlbCache.load;
}

export async function loadLocalMlbBoard({ lineType = "regular", gameIds = [], onProgress, onFirstPage, onPage, isCurrent } = {}) {
  const local = await loadLocalMlbCandidate();
  if (!local) throw new Error("Local MLB candidate or promotion evidence is unavailable.");
  if (isCurrent && !isCurrent()) return { props: [], games: local.games, version: local.version, page: { cancelled: true } };
  const ids = new Set((Array.isArray(gameIds) ? gameIds : []).map(String).filter(Boolean));
  const normalizedType = String(lineType || "regular").toLowerCase();
  const rows = local.props
    .filter((row) => !ids.size || ids.has(String(row?.["Game ID"] || "")))
    .filter((row) => normalizedType === "all" || localLineType(row) === normalizedType)
    .map((row) => ({ ...row, __pwgVersion: local.version.version_id }));
  const result = {
    props: rows,
    games: local.games,
    injuryReport: local.injuryReport,
    version: local.version,
    localCandidate: local.manifest,
    page: { loaded: rows.length, total: rows.length, pages: 1 },
  };
  onProgress?.(result.page);
  onFirstPage?.(result);
  onPage?.(result);
  return result;
}

async function loadLocalMlbProjection(row, version = "") {
  const local = await loadLocalMlbCandidate();
  if (!local || String(version) !== String(local.version.version_id)) {
    throw new Error("Local MLB candidate version mismatch.");
  }
  return local.props.find((candidate) => projectionIdOf(candidate) === projectionIdOf(row)) || null;
}

const timezoneAwareTimestamp = (value) => {
  const raw = String(value || "").trim();
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(raw)) return null;
  const milliseconds = Date.parse(raw);
  return Number.isFinite(milliseconds) ? milliseconds : null;
};

export function validateLocalSoccerCandidate(payload, manifest, checksum, captureTime = Date.now()) {
  if (!manifest || manifest.schema_version !== "PWG_SOCCER_LOCAL_REVIEW_V1" || manifest.status !== "VERIFIED") {
    throw new Error("Local Soccer promotion evidence is missing or invalid.");
  }
  if (!SHA256.test(String(manifest.candidate_checksum || "")) || checksum !== manifest.candidate_checksum) {
    throw new Error("Local Soccer candidate checksum does not match promotion evidence.");
  }
  if (!manifest.acquisition_id || !Number.isFinite(Number(captureTime))) {
    throw new Error("Local Soccer candidate identity or review cutoff is invalid.");
  }
  const rows = Array.isArray(payload?.props) ? payload.props : [];
  const ids = rows.map(projectionIdOf);
  const uniqueIds = new Set(ids);
  if (!rows.length || ids.some((value) => !value) || uniqueIds.size !== rows.length) {
    throw new Error("Local Soccer candidate Projection IDs are missing or duplicated.");
  }
  if (rows.length !== Number(manifest.row_count) || uniqueIds.size !== Number(manifest.projection_id_count)) {
    throw new Error("Local Soccer candidate row or Projection-ID count does not match promotion evidence.");
  }

  const fixtures = new Map();
  for (const row of rows) {
    const fixtureId = String(row?.["Fixture ID"] || row?.["Game ID"] || "").trim();
    if (!fixtures.has(fixtureId)) fixtures.set(fixtureId, []);
    fixtures.get(fixtureId).push(row);
  }
  const upcoming = [];
  let upcomingFixtureCount = 0;
  for (const [fixtureId, fixtureRows] of fixtures) {
    const timestamps = new Set(fixtureRows.map((row) => timezoneAwareTimestamp(row?.["Game Start Timestamp"])));
    if (!fixtureId || timestamps.size !== 1 || timestamps.has(null)) continue;
    const [kickoff] = timestamps;
    if (kickoff <= Number(captureTime)) continue;
    upcomingFixtureCount += 1;
    upcoming.push(...fixtureRows);
  }
  return { rows: upcoming, upcomingFixtureCount };
}

export async function loadLocalSoccerCandidate() {
  if (!isLocalMlbOrigin()) return null;
  if (localSoccerCache.payload) return localSoccerCache.payload;
  if (localSoccerCache.load) return localSoccerCache.load;
  const captureTime = Date.now();
  localSoccerCache.load = Promise.all([
    fetch(LOCAL_SOCCER_CANDIDATE_PATH, { cache: "no-store", credentials: "same-origin" }),
    fetch(LOCAL_SOCCER_MANIFEST_PATH, { cache: "no-store", credentials: "same-origin" }),
  ]).then(async ([candidateResponse, manifestResponse]) => {
    if (!candidateResponse.ok || !manifestResponse.ok) {
      throw new Error("Local Soccer candidate or promotion evidence is unavailable.");
    }
    const [candidate, manifest] = await Promise.all([
      sha256Response(candidateResponse),
      manifestResponse.json(),
    ]);
    let payload;
    try { payload = JSON.parse(candidate.text); } catch { throw new Error("Local Soccer candidate is malformed."); }
    const validated = validateLocalSoccerCandidate(payload, manifest, candidate.checksum, captureTime);
    const value = {
      props: validated.rows,
      injuryReport: Array.isArray(payload?.injury_report) ? payload.injury_report : [],
      localCandidate: {
        ...manifest,
        review_cutoff_utc: new Date(captureTime).toISOString(),
        upcoming_row_count: validated.rows.length,
        upcoming_fixture_count: validated.upcomingFixtureCount,
      },
    };
    localSoccerCache.payload = value;
    return value;
  }).finally(() => { localSoccerCache.load = null; });
  return localSoccerCache.load;
}

export function validateLocalNflCandidate(payload, manifest, checksum) {
  if (!manifest || manifest.schema_version !== "PWG_NFL_LOCAL_REVIEW_V1" || manifest.status !== "VERIFIED") {
    throw new Error("Local NFL promotion evidence is missing or invalid.");
  }
  if (!SHA256.test(String(manifest.candidate_checksum || "")) || checksum !== manifest.candidate_checksum) {
    throw new Error("Local NFL candidate checksum does not match promotion evidence.");
  }
  if (!manifest.acquisition_id) throw new Error("Local NFL candidate identity is incomplete.");
  const rows = Array.isArray(payload?.props) ? payload.props : [];
  const ids = rows.map(projectionIdOf);
  const uniqueIds = new Set(ids);
  if (!rows.length || ids.some((value) => !value) || uniqueIds.size !== rows.length) {
    throw new Error("Local NFL candidate Projection IDs are missing or duplicated.");
  }
  if (rows.length !== Number(manifest.row_count) || uniqueIds.size !== Number(manifest.projection_id_count)) {
    throw new Error("Local NFL candidate row or Projection-ID count does not match promotion evidence.");
  }
  return rows;
}

export async function loadLocalNflCandidate() {
  if (!isLocalMlbOrigin()) return null;
  if (localNflCache.payload) return localNflCache.payload;
  if (localNflCache.load) return localNflCache.load;
  localNflCache.load = Promise.all([
    fetch(LOCAL_NFL_CANDIDATE_PATH, { cache: "no-store", credentials: "same-origin" }),
    fetch(LOCAL_NFL_MANIFEST_PATH, { cache: "no-store", credentials: "same-origin" }),
  ]).then(async ([candidateResponse, manifestResponse]) => {
    if (!candidateResponse.ok || !manifestResponse.ok) throw new Error("Local NFL candidate or promotion evidence is unavailable.");
    const [candidate, manifest] = await Promise.all([sha256Response(candidateResponse), manifestResponse.json()]);
    let payload;
    try { payload = JSON.parse(candidate.text); } catch { throw new Error("Local NFL candidate is malformed."); }
    const rows = validateLocalNflCandidate(payload, manifest, candidate.checksum);
    const value = {
      props: rows,
      injuryReport: Array.isArray(payload?.injury_report) ? payload.injury_report : [],
      localCandidate: manifest,
    };
    localNflCache.payload = value;
    return value;
  }).finally(() => { localNflCache.load = null; });
  return localNflCache.load;
}

const normalizePayload = (data) => ({
  props: Array.isArray(data) ? data : Array.isArray(data?.props) ? data.props : [],
  injuryReport: Array.isArray(data?.injury_report)
    ? data.injury_report
    : Array.isArray(data?.injuryReport) ? data.injuryReport : [],
  games: Array.isArray(data?.games) ? data.games : [],
  version: data?.version || null,
});

const inflightAuthenticatedRequests = new Map();
const RETRYABLE_READ_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const retryReadDelay = () => new Promise((resolve) => setTimeout(resolve, 250));

async function performAuthenticatedFetch(path, { refresh = false, retry = true, signal } = {}) {
  const user = auth.currentUser;
  if (!user) {
    const error = new Error("Sign in is required to load sports data.");
    error.status = 401;
    throw error;
  }
  const token = await user.getIdToken(refresh);
  const response = await fetch(`${SECURE_SPORTS_API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    // Authentication is exclusively the Firebase bearer token. Sending
    // cross-origin cookies would require an unrelated credentialed-CORS
    // contract and causes browsers to reject otherwise valid API responses.
    credentials: "omit",
    cache: "no-store",
    signal,
  });
  if (response.status === 401 && !refresh) return performAuthenticatedFetch(path, { refresh: true, signal });
  if (retry && RETRYABLE_READ_STATUSES.has(response.status)) {
    await retryReadDelay();
    return performAuthenticatedFetch(path, { refresh, retry: false, signal });
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || "Sports data temporarily unavailable.");
    error.status = response.status;
    throw error;
  }
  return payload;
}

function authenticatedFetch(path, options = {}) {
  if (options.refresh) return performAuthenticatedFetch(path, options);
  const user = auth.currentUser;
  const key = `${user?.uid || "current"}:${path}`;
  if (inflightAuthenticatedRequests.has(key)) return inflightAuthenticatedRequests.get(key);
  const request = performAuthenticatedFetch(path, options)
    .finally(() => inflightAuthenticatedRequests.delete(key));
  inflightAuthenticatedRequests.set(key, request);
  return request;
}

export async function loadSecureSport(sport, options = {}) {
  const normalized = String(sport || "").trim().toLowerCase();
  if (!/^[a-z0-9]+$/.test(normalized)) throw new Error("Unknown sport.");
  if (normalized === "mlb") return loadMlbBoard(options);
  if (productionStaticCustomerSportEnabled(normalized)) {
    return loadStaticCustomerSport(normalized);
  }
  if (isProductionDashboardHost()) {
    const offering = productionOffering(normalized);
    if (!offering) throw new Error(`${normalized.toUpperCase()} does not have a governed production release.`);
    throw new Error(`${normalized.toUpperCase()} governed production loader is unavailable.`);
  }
  return normalizePayload(await authenticatedFetch(`/sports/${normalized}`));
}

export async function loadSecureMlbVersion() {
  if (!SECURE_SPORTS_API_CONFIGURED) {
    const error = new Error("The dedicated MLB sports-data service is not configured.");
    error.status = 503;
    throw error;
  }
  return authenticatedFetch("/sports/mlb/version");
}

export async function loadSecureMlbProps(version, query = {}) {
  const versionId = String(version || "").trim();
  if (!versionId) throw new Error("A sports-data version is required.");
  const allowed = ["game_id", "market", "line_type", "q", "cursor", "limit"];
  const parameters = new URLSearchParams({ version: versionId });
  for (const key of allowed) {
    if (query[key] !== undefined && query[key] !== null && query[key] !== "") parameters.set(key, String(query[key]));
  }
  return authenticatedFetch(`/sports/mlb/props?${parameters.toString()}`);
}

export async function loadSecureMlbGames(version, query = {}) {
  const versionId = String(version || "").trim();
  if (!versionId) throw new Error("A sports-data version is required.");
  const parameters = new URLSearchParams({ version: versionId, limit: "100" });
  if (query.cursor !== undefined && query.cursor !== null && query.cursor !== "") {
    parameters.set("cursor", String(query.cursor));
  }
  return authenticatedFetch(`/sports/mlb/games?${parameters.toString()}`);
}

export function mergeProjectionPages(pages) {
  const seen = new Set();
  const rows = [];
  for (const page of Array.isArray(pages) ? pages : []) {
    for (const row of Array.isArray(page?.props) ? page.props : []) {
      const id = String(row?.["Projection ID"] ?? row?.ProjectionID ?? row?.projection_id ?? "").trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      rows.push(row);
    }
  }
  return rows;
}

export async function loadAllSecureMlbProps(version, query = {}, { onProgress, onPage, isCurrent } = {}) {
  const pages = [];
  let cursor = query.cursor ?? 0;
  let total = null;
  do {
    if (isCurrent && !isCurrent()) return { props: [], page: { cancelled: true } };
    const page = await loadSecureMlbProps(version, { ...query, cursor, limit: 100 });
    if (isCurrent && !isCurrent()) return { props: [], page: { cancelled: true } };
    pages.push(page);
    total = Number.isFinite(Number(page?.page?.total)) ? Number(page.page.total) : total;
    const loaded = mergeProjectionPages(pages).length;
    onProgress?.({ loaded, total, pages: pages.length });
    await onPage?.({ props: mergeProjectionPages(pages), page: page?.page || {}, pages: pages.length });
    cursor = page?.page?.next_cursor;
  } while (cursor !== null && cursor !== undefined && cursor !== "");
  const props = mergeProjectionPages(pages);
  return { props, page: { total: total ?? props.length, loaded: props.length, pages: pages.length } };
}

const adminMlbVersionId = (payload) =>
  String(payload?.version_id || payload?.version?.version_id || "").trim();

const adminMlbGameId = (value) =>
  String(value?.game_id ?? value?.["Game ID"] ?? value ?? "").trim();

const adminMlbVersionMismatch = () => {
  const error = new Error("The MLB admin release changed while it was loading. Refresh THEO and try again.");
  error.status = 409;
  return error;
};

const snapshotAdminMlbGames = (games) =>
  games.map((game) => ({
    ...game,
    line_type_counts: { ...game.line_type_counts },
  }));

const adminMlbShardCache = {
  context: "",
  shards: new Map(),
};

export function clearAdminMlbShardCache() {
  adminMlbShardCache.context = "";
  adminMlbShardCache.shards.clear();
}

const configureAdminMlbShardCache = (versionId) => {
  const uid = String(auth.currentUser?.uid || "").trim();
  const context = `${uid}|${String(versionId || "").trim()}`;
  if (!uid || adminMlbShardCache.context !== context) {
    clearAdminMlbShardCache();
    adminMlbShardCache.context = context;
  }
};

const visibleAdminMlbRows = (rows, normalizedType, versionId) =>
  (Array.isArray(rows) ? rows : [])
    .filter((row) => normalizedType === "all" || localLineType(row) === normalizedType)
    .map((row) => ({
      ...row,
      __pwgVersion: versionId,
      __pwgAdmin: true,
    }));

export async function loadAdminMlbBoard({
  versionId: requestedVersion = "",
  lineType = "regular",
  gameIds = [],
  onProgress,
  onFirstPage,
  onPage,
  isCurrent,
} = {}) {
  const versionPayload = await authenticatedFetch("/admin/sports/mlb/version");
  const versionId = adminMlbVersionId(versionPayload);

  if (!versionId) throw new Error("The MLB admin release is unavailable.");
  if (requestedVersion && String(requestedVersion) !== versionId) {
    throw adminMlbVersionMismatch();
  }
  configureAdminMlbShardCache(versionId);

  const gamesPayload = await authenticatedFetch("/admin/sports/mlb/games");
  if (adminMlbVersionId(gamesPayload) !== versionId) {
    throw adminMlbVersionMismatch();
  }

  const requestedIds = new Set(
    (Array.isArray(gameIds) ? gameIds : [])
      .map((gameId) => String(gameId || "").trim())
      .filter(Boolean)
  );

  const gameDescriptors = (Array.isArray(gamesPayload?.games) ? gamesPayload.games : [])
    .map((game) => {
      const gameId = adminMlbGameId(game);
      return {
        ...game,
        game_id: gameId,
        "Game ID": gameId,
        line_type_counts: { ...game?.line_type_counts },
      };
    })
    .filter((game) => game.game_id);

  // The game index is the immutable inventory for the loaded admin release.
  // A game selection scopes only the shards/rows fetched for cards; it must
  // never shrink the drawer options returned to the caller.
  const selectedGameDescriptors = gameDescriptors
    .filter((game) => !requestedIds.size || requestedIds.has(game.game_id));

  const normalizedType = String(lineType || "regular").trim().toLowerCase();
  const collectedPages = selectedGameDescriptors
    .filter((game) => adminMlbShardCache.shards.has(game.game_id))
    .map((game) => ({
      props: visibleAdminMlbRows(
        adminMlbShardCache.shards.get(game.game_id),
        normalizedType,
        versionId
      ),
    }));
  let firstPagePublished = false;
  let rawLoaded = selectedGameDescriptors
    .filter((game) => adminMlbShardCache.shards.has(game.game_id))
    .reduce((sum, game) => sum + adminMlbShardCache.shards.get(game.game_id).length, 0);
  const rawTotal = selectedGameDescriptors.reduce(
    (sum, game) => sum + Number(game?.rows || game?.row_count || 0),
    0
  );

  const publish = () => {
    const rows = mergeProjectionPages(collectedPages);
    const games = snapshotAdminMlbGames(gameDescriptors);
    const payload = {
      props: rows,
      games,
      injuryReport: [],
      version: {
        version_id: versionId,
        generated_at: versionPayload?.generated_at || gamesPayload?.generated_at || null,
        row_count: versionPayload?.row_count ?? null,
        game_count: versionPayload?.game_count ?? gameDescriptors.length,
      },
      page: {
        loaded: rows.length,
        total: normalizedType === "all" ? rawTotal : null,
        raw_loaded: rawLoaded,
        raw_total: rawTotal,
      },
    };

    onProgress?.(payload.page);
    onPage?.(payload);

    if (!firstPagePublished) {
      firstPagePublished = true;
      onFirstPage?.(payload);
    }

    return payload;
  };

  // Publish the complete canonical game index before opening any game shard.
  // This matches the regular-user delivery strategy: index first, rows second.
  publish();

  for (const game of selectedGameDescriptors.filter((item) => !adminMlbShardCache.shards.has(item.game_id))) {
    if (isCurrent && !isCurrent()) {
      return {
        props: [],
        games: snapshotAdminMlbGames(gameDescriptors),
        version: { version_id: versionId },
        page: { cancelled: true },
      };
    }

    let shard;
    try {
      shard = await authenticatedFetch(
        `/admin/sports/mlb/games/${encodeURIComponent(game.game_id)}`
      );
    } catch (error) {
      if (!collectedPages.length) throw error;
      const payload = publish();
      return {
        ...payload,
        page: {
          ...payload.page,
          partial_error: `Unable to load selected MLB game ${game.game_id}.`,
        },
      };
    }

    if (isCurrent && !isCurrent()) {
      return {
        props: mergeProjectionPages(collectedPages),
        games: snapshotAdminMlbGames(gameDescriptors),
        version: { version_id: versionId },
        page: { cancelled: true },
      };
    }

    if (
      adminMlbVersionId(shard) !== versionId ||
      adminMlbGameId(shard) !== game.game_id
    ) {
      throw adminMlbVersionMismatch();
    }

    const rawRows = Array.isArray(shard?.props) ? shard.props : [];
    adminMlbShardCache.shards.set(
      game.game_id,
      rawRows.map((row) => ({ ...row }))
    );
    rawLoaded += rawRows.length;

    const visibleRows = visibleAdminMlbRows(rawRows, normalizedType, versionId);

    collectedPages.push({ props: visibleRows });
    publish();
  }

  if (!firstPagePublished) return publish();

  return {
    props: mergeProjectionPages(collectedPages),
    games: snapshotAdminMlbGames(gameDescriptors),
    injuryReport: [],
    version: {
      version_id: versionId,
      generated_at: versionPayload?.generated_at || gamesPayload?.generated_at || null,
      row_count: versionPayload?.row_count ?? null,
      game_count: versionPayload?.game_count ?? gameDescriptors.length,
    },
    page: {
      loaded: mergeProjectionPages(collectedPages).length,
      total: normalizedType === "all" ? rawTotal : null,
      raw_loaded: rawLoaded,
      raw_total: rawTotal,
    },
  };
}


export async function loadSecureMlbBoard({ versionId: pinnedVersion = "", lineType = "regular", gameIds = [], onProgress, onFirstPage, onPage, isCurrent } = {}) {
  if (isLocalMlbOrigin()) {
    return loadLocalMlbBoard({ versionId: pinnedVersion, lineType, gameIds, onProgress, onFirstPage, onPage, isCurrent });
  }
  const version = pinnedVersion ? { version: { version_id: pinnedVersion } } : await loadSecureMlbVersion();
  const versionId = version?.version?.version_id;
  // Games and the first props page are independent once the release is pinned.
  // Start both immediately so the sidebar never waits behind props pagination.
  const gamesPromise = loadSecureMlbGames(versionId);
  const ids = [...new Set((Array.isArray(gameIds) ? gameIds : []).map(String).filter(Boolean))];
  const queries = ids.length ? ids.map((gameId) => ({ game_id: gameId })) : [{}];
  const pageResults = [];
  let loaded = 0;
  let total = 0;
  let firstPagePublished = false;
  for (const gameQuery of queries) {
    const result = await loadAllSecureMlbProps(versionId, { ...gameQuery, line_type: lineType }, {
      isCurrent,
      onProgress: (progress) => {
        onProgress?.({ loaded: loaded + progress.loaded, total: total + (progress.total ?? 0), pages: progress.pages });
      },
      onPage: async (page) => {
        if (isCurrent && !isCurrent()) return;
        const rows = mergeProjectionPages([...pageResults, { props: page.props }])
          .map((row) => ({ ...row, __pwgVersion: versionId }));
        const gamesPayload = await gamesPromise;
        const payload = {
          props: rows,
          games: gamesPayload.games || [],
          injuryReport: [],
          version: gamesPayload.version || version.version || null,
          page: { loaded: rows.length, total: page.page?.total ?? null, pages: page.pages },
        };
        onPage?.(payload);
        if (!firstPagePublished) {
          firstPagePublished = true;
          onFirstPage?.(payload);
        }
      },
    });
    if (result?.page?.cancelled) {
      const gamesPayload = await gamesPromise;
      return { props: [], games: gamesPayload.games || [], version: gamesPayload.version || version.version, page: result.page };
    }
    pageResults.push({ props: result.props });
    loaded += result.props.length;
    total += result.page.total;
  }
  const gamesPayload = await gamesPromise;
  return {
    props: mergeProjectionPages(pageResults).map((row) => ({ ...row, __pwgVersion: versionId })),
    games: gamesPayload.games || [],
    injuryReport: [],
    version: gamesPayload.version || version.version || null,
    page: { loaded, total },
  };
}

export async function loadSecureProjection(sport, projectionId, version = "") {
  const normalized = String(sport || "").trim().toLowerCase();
  const identity = String(projectionId || "").trim();
  if (!/^[a-z0-9]+$/.test(normalized) || !identity || identity.includes("/")) return null;
  const versionParameter = normalized === "mlb"
    ? `?${new URLSearchParams({ version: String(version || "").trim() }).toString()}`
    : "";
  if (normalized === "mlb" && !String(version || "").trim()) {
    throw new Error("A sports-data version is required.");
  }
  if (normalized === "mlb" && isLocalMlbOrigin()) {
    const local = await loadLocalMlbCandidate();
    if (!local || version !== local.version.version_id) throw new Error("Local MLB candidate version mismatch.");
    return local.props.find((row) => projectionIdOf(row) === identity) || null;
  }
  const payload = await authenticatedFetch(`/sports/${normalized}/projections/${encodeURIComponent(identity)}${versionParameter}`);
  return payload?.prop || null;
}

export const __resetSecureSportsDataForTests = () => {
  __resetMlbStaticDataForTests();
  mlbCache.versionId = "";
  mlbCache.payload = null;
  mlbCache.load = null;
  mlbCache.pages.clear();
  mlbCache.generation += 1;
  localMlbCache.payload = null;
  localMlbCache.load = null;
  localSoccerCache.payload = null;
  localSoccerCache.load = null;
  localNflCache.payload = null;
  localNflCache.load = null;
  localMlbOriginOverride = null;
  mlbRuntimeOverride = null;
  clearAdminMlbShardCache();
};

export const __setLocalMlbOriginForTests = (value) => { localMlbOriginOverride = Boolean(value); };
export const __setMlbRuntimeForTests = (value) => { mlbRuntimeOverride = value; };
